/**
 * R6 A3 — the client's tap-to-verdict instrument, and the only thing on this path that measures
 * the span the resident actually experiences.
 *
 * **Why it has to exist at all.** `measurements.md` R1 is the gap that makes an unexplained ~8 s
 * failed open un-attributable: `tappedAt` exists on both screens and feeds only D189(b)'s 3 s
 * gesture budget, no `performance.now()` is taken anywhere, and nothing logs a wall time. The BLE
 * adapters do measure `latencyMs` and upload it in the receipt, so the server holds a
 * client-measured **BLE** distribution — while **cloud-path client latency is measured nowhere, on
 * either side of the wire.** Every number in `measurements.md` §1.1 is the server's own span
 * (publish → verdict), which by construction cannot see the request, the response, or the render.
 *
 * **It adds no field, no endpoint and no sentence**, which is what makes it Phase-A work rather
 * than a Phase-B candidate (R6 §3 A3, §6: *"the instrument lines in A3 and A7 are the whole of what
 * Phase A builds"*). Nothing here is read by the product; the consumer is a device log pipe.
 *
 * ## Five decisions, each of which is the finding rather than the code
 *
 *  - **One line per LEG, not per tap, joined by {@link OpenTiming.tapId}.** A tap that fails over
 *    the cloud and then dispatches over BLE is one tap and two waits, and R1's whole question is
 *    *which* of them owns the unexplained time. A single line per tap reports 8 s and names
 *    nothing; two lines sharing a `tapId` report 4.2 s and 8.1 s, and the composition is named.
 *    `elapsedMs` is always measured **from the tap**, so a leg's own cost is the difference between
 *    consecutive lines and needs no second field to be wrong about. **A standing fallback offer is
 *    the ABSENCE of a second leg**, not a leg of its own: the cloud line already carries how long
 *    the resident waited to be told anything, which is the number O8 needs, and pressing the offer
 *    is the resident's own next gesture and so its own tap with its own `tapId`.
 *  - **`settle()` is idempotent, and that is not tidiness.** Both screens leave `runCommand`
 *    through a try, a catch, a `finally` and a rethrow, and several of those paths overlap — the
 *    rethrow path settles the offer *and* falls through. A second emit for one leg would
 *    double-count that leg in every distribution read off this line, which is the class of defect
 *    that makes an instrument worse than none. The first settle wins and the rest are dropped.
 *  - **`performance.now()`, never `Date.now()`.** This is a duration, and `Date.now()` is a wall
 *    clock an NTP correction or a manual change can move underneath a 20-second window — which is
 *    exactly the length of window this exists to measure. The screens' own `tappedAt` stays
 *    `Date.now()`: it is compared against `FALLBACK_OFFER_MS` by `decideFallback` and is not this.
 *  - **Always on, with no flag.** A gate behind an env read is a way for the measurement to
 *    silently not run, and this repository has paid for that shape twice — `MqttConnectLatency`'s
 *    emission is asserted nowhere, and a metric that stops emitting reads exactly like a stage with
 *    no traffic. One line per tap is not a volume problem, and the line names nothing private.
 *  - **`console.info(JSON.stringify(…))`, deviating from the client idiom on purpose.** `web/`
 *    elsewhere writes `console.warn('event-name', object)`, which DevTools renders well and a
 *    **device log pipe does not**: Capacitor's Android bridge forwards a console message as a
 *    string, so an object argument is one bridge implementation away from `[object Object]`. A3's
 *    stated readers are `xcrun devicectl device process launch --console` and `adb logcat`, so the
 *    line is one greppable JSON string. `info` rather than `warn` because nothing is wrong.
 *
 * ## What must never go on this line
 *
 * **Names and numbers only** (R6 §3 A3, and `AGENTS.md` §4.9's logging rule, whose subject is the
 * same device log): never the gate password, never the bearer, never `deviceId`. `gateId` **is**
 * carried — it is eight digits that grant nothing (`AGENTS.md` §4.1), it is what joins a client
 * line to the server's own audit row and to A1's per-gate distributions, and H6 is a question about
 * one gate. It is the one identifier here and it is deliberate.
 */

/** Which screen dispatched the tap. Home and Gate Detail cost different things (R6 §1.1 row 1b). */
export type OpenTimingSurface = 'home' | 'gate-detail'

/**
 * Which radio carried this leg.
 *
 * `'none'` is a real value and not a gap: a plan with no transport, or D264's diverted tap that
 * never reached the network, is a leg that consumed a wait and delivered a refusal — and the time
 * it spent is a number the resident paid.
 */
export type OpenTimingTransport = 'cloud' | 'ble' | 'none'

/**
 * What the resident got out of this leg.
 *
 * Deliberately **the screen's vocabulary and not the server's**: `DeliveryOutcome` lives one wire
 * away and is not what a tap ended in. `GateOpenOutcome` was the other candidate and is too coarse
 * — three values that cannot tell PEC-8's *unconfirmed* from an access refusal, which is the
 * distinction the failure taxonomy (R6 §2) is entirely about.
 */
export type OpenTimingVerdict =
  /** Success on screen — D223's set, so the two `409`s and a `CMD:Err` are here too. */
  | 'opened'
  /** PEC-8: delivery not confirmed. The gate may have opened. */
  | 'unconfirmed'
  /** An access decision — a `DenyReason`, or `not-authorized`. Nothing was delivered. */
  | 'refused'
  /**
   * Anything else that failed: a 5xx, an unrecognized reason, a thrown non-`NetworkError`, or any
   * local BLE failure. **The detail is `reason`, not a sixth verdict** — `transport` already tells a
   * radio refusal from a server one, and a union that grew a member per cause would be the failure
   * taxonomy (R6 §2) duplicated in an instrument, which is the copy that drifts.
   */
  | 'failed'

/** One leg of one tap: which radio carried it, what the resident got, and why. */
export interface OpenTimingLeg {
  readonly transport: OpenTimingTransport
  readonly verdict: OpenTimingVerdict
  /**
   * The cause, verbatim from whichever union already names it — a `LocalFailureReason`, a
   * `DenyReason`, an `OpenResponse` reason, or one of this instrument's own two words for a state
   * no union names (`'threw'`, `'no-transport'`).
   *
   * **It is carried because counting R6 §2's F-rows is the whole of O7's gate**, and `verdict`
   * alone collapses five of them into `'failed'`. It is a **name**, never a message and never a
   * payload: every value assigned to it is a literal from a closed union in this repository, so no
   * error text, no server sentence and nothing a user typed can reach it.
   */
  readonly reason?: string
}

export interface OpenTiming {
  /** Joins the legs of one tap. Short, random, and meaningless outside this log. */
  readonly tapId: string
  /**
   * End this leg and emit its line. **Idempotent** — the first call wins, later ones are dropped,
   * because the screens' exits overlap and a double emit double-counts the leg.
   */
  settle(leg: OpenTimingLeg): void
  /**
   * A second leg of the **same** tap, timed from the **original tap** rather than from now.
   *
   * This is what makes an automatic BLE fallback readable: `elapsedMs` on the BLE line is the whole
   * span the resident waited, cloud leg included, so the radio's own cost is the difference against
   * the cloud line. Timing a second leg from its own start would report two small numbers and lose
   * the sum, which is precisely the quantity R1 is missing.
   *
   * The origin lives on the token rather than being threaded by the caller, because the screens
   * already thread a `tappedAt` through `settleFallback` / `offerOrNothing` for D189(b)'s gesture
   * budget and a second thing to thread is how the two drift apart.
   */
  nextLeg(): OpenTiming
}

/**
 * Begin timing one tap.
 *
 * Call it at the point past which a command genuinely leaves the device — the same line the
 * overlay is raised on (D229: *"the raise sits past `commandDisabled`"*), so the two agree about
 * what a tap is. An access precheck that refuses before dispatch is not a tap this measures.
 */
export function beginOpenTiming(input: {
  readonly surface: OpenTimingSurface
  readonly gateId: string
}): OpenTiming {
  return leg({ ...input, tapId: newTapId(), startedAt: performance.now() })
}

function leg(context: {
  readonly surface: OpenTimingSurface
  readonly gateId: string
  readonly tapId: string
  readonly startedAt: number
}): OpenTiming {
  let settled = false
  return {
    tapId: context.tapId,
    settle(next) {
      if (settled) return
      settled = true
      emit({
        tapId: context.tapId,
        surface: context.surface,
        gateId: context.gateId,
        transport: next.transport,
        verdict: next.verdict,
        ...(next.reason === undefined ? {} : { reason: next.reason }),
        elapsedMs: Math.round(performance.now() - context.startedAt),
      })
    },
    nextLeg: () => leg(context),
  }
}

/**
 * 🔸 Eight hex characters, from `crypto.getRandomValues` where it exists.
 *
 * It joins the legs of one tap **inside one log capture** and nothing else — not stored, not
 * uploaded, and not an identifier of anything. `globalThis.crypto` is the CSPRNG on both sides
 * here for the same reason `packages/core` relies on it, and the `Math.random` fallback exists
 * because a `crypto`-less surface must degrade to a slightly collidable id rather than to no line
 * at all: a missing line is a hole in the distribution, while a collision inside one capture is
 * visible as two legs sharing an id.
 */
function newTapId(): string {
  const cryptoApi = globalThis.crypto as Crypto | undefined
  if (cryptoApi?.getRandomValues !== undefined) {
    const bytes = new Uint8Array(4)
    cryptoApi.getRandomValues(bytes)
    return [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('')
  }
  return Math.random().toString(16).slice(2, 10).padStart(8, '0')
}

function emit(fields: {
  readonly tapId: string
  readonly surface: OpenTimingSurface
  readonly gateId: string
  readonly transport: OpenTimingTransport
  readonly verdict: OpenTimingVerdict
  readonly reason?: string
  readonly elapsedMs: number
}): void {
  // A throwing instrument must never take a tap down with it. `performance`, `crypto` and
  // `console` are all present on every surface this ships to, so this catch is expected to be
  // dead — which is exactly the standard for a line that runs on every open.
  try {
    console.info(JSON.stringify({ level: 'info', event: 'open-timing', ...fields }))
  } catch {
    /* An unreadable measurement is not worth a failed open. */
  }
}
